import Swiper from 'swiper';
import {Navigation, Pagination, Autoplay, A11y, EffectFade, Grid} from "swiper/modules";

function equalizeSlideHeights(swiper) {
    const slides = swiper.slides;
    slides.forEach(s => (s.style.height = ''));
    const max = Math.max(...Array.from(slides).map(s => s.offsetHeight));
    slides.forEach(s => (s.style.height = max + 'px'));
}

// Named configuration presets; extend as needed.
const PRESETS = {
    default: {
        slidesPerView: 1,
        spaceBetween: 16,
        pagination: {clickable: true},
        navigation: true,
        a11y: {enabled: true}
    },
	team: {
		modules: [Grid],
		slidesPerView: 1.2,
		spaceBetween: 24,
		breakpoints: {
			768:{
				enabled: false,
				spaceBetween: 24,
				slidesOffsetBefore: 0,
				slidesOffsetAfter: 0,
				slidesPerView: 2,
				grid: {
					cols: 2,
					rows: 3,
					fill: 'row',
				}
			},
			992: {
				enabled: false,
				spaceBetween: 24,
				slidesOffsetBefore: 0,
				slidesOffsetAfter: 0,
				slidesPerView: 3,
				centerInsufficientSlides: true,
				grid: {
					cols: 3,
					rows: 2,
					fill: 'row',
				}
			}
		},
		on: {
			//init: this.equalizeSwiperSlideHeights.bind(this),
			//resize: this.equalizeSwiperSlideHeights.bind(this),
		},
        navigation: {
            enabled: true,
            nextEl: '#swiper-logoslider-button-next',
            prevEl: '#swiper-logoslider-button-prev',
        }
    },
	logos: {
        slidesPerView: 2.5,
        spaceBetween: 24,
		breakpoints: {
            578: {
                slidesPerView: 4.5,
            },
            992: {
                slidesPerView: 6,
                centerInsufficientSlides: true,
            }
        },
        navigation: {
            enabled: true,
            nextEl: '#swiper-logoslider-button-next',
            prevEl: '#swiper-logoslider-button-prev',
        }
    },
	articles: {
        slidesPerView: 1.1,
        spaceBetween: 24,
		breakpoints: {
            578: {
                slidesPerView: 2.2,
            },
            992: {
                slidesPerView: 3,
				centerInsufficientSlides: true,
            }
        },
        navigation: {
            enabled: true,
            nextEl: '#swiper-article-button-next',
            prevEl: '#swiper-article-button-prev',
        },
        on: {
            init: (swiper) => equalizeSlideHeights(swiper),
            resize: (swiper) => equalizeSlideHeights(swiper),
        }
    },
    currentoffers: {
        slidesPerView: 1.1,
        spaceBetween: 24,
		centeredSlides: true,
		breakpoints: {
            578: {
                slidesPerView: 2.2,
				initialSlide: 1,

            },
            992: {
                slidesPerView: 3,
				initialSlide: 1,
				centerInsufficientSlides: true,
            }
        },
        navigation: {
            enabled: true,
            nextEl: '#swiper-co-button-next',
            prevEl: '#swiper-co-button-prev',
        }
    },
    testimonials: {
        slidesPerView: 1.1,
        spaceBetween: 24,
        centeredSlides: true,
        initialSlide: 1,
		breakpoints: {
            578: {
                slidesPerView: 2.2,
            },
            992: {
                slidesPerView: 3,
            }
        },
        navigation: {
            enabled: true,
            nextEl: '#swiper-t-button-next',
            prevEl: '#swiper-t-button-prev',
        },
    },
};

export default class SwiperWidget {
    constructor(el, options = {}) {
        this.el = el;
        this.options = options;
        this.instance = null;
        // Fire and forget; loader does not await
        this.init();
    }

    async init() {
        if (!this.el.classList.contains('swiper')) return;

        const presetName = this.el.getAttribute('data-swiper-preset') || this.options.preset || 'default';
        const preset = PRESETS[presetName] || PRESETS.default;

        // Allow JSON config override via data-swiper-options
        let dataOptions = {};
        const raw = this.el.getAttribute('data-swiper-options');
        if (raw) {
            try {
                dataOptions = JSON.parse(raw);
            } catch (_) { /* ignore invalid JSON */
            }
        }

        const config = {...preset, ...this.options, ...dataOptions};

        // Find navigation/pagination targets if present
        const nextEl = this.el.querySelector('[data-swiper-next]');
        const prevEl = this.el.querySelector('[data-swiper-prev]');
        const paginationEl = this.el.querySelector('[data-swiper-pagination]');

        if (config.navigation === true) {
            config.navigation = {};
            if (nextEl) config.navigation.nextEl = nextEl;
            if (prevEl) config.navigation.prevEl = prevEl;
        }
        if (config.pagination === true) {
            config.pagination = {el: paginationEl || this.el.querySelector('.swiper-pagination'), clickable: true};
        }

        // Lazy-load Swiper core and modules
        try {
            const usedModules = [];
            if (config.navigation) usedModules.push(Navigation);
            if (config.pagination) usedModules.push(Pagination);
            if (config.autoplay) usedModules.push(Autoplay);
            if (config.a11y) usedModules.push(A11y);
			if (config.grid) usedModules.push(Grid);
			// Check if modules are already defined in preset (e.g., Grid for team preset)
			if (config.modules && config.modules.length > 0) {
				config.modules.forEach(m => {
					if (!usedModules.includes(m)) {
						usedModules.push(m);
					}
				});
			}
            if (config.effect === 'fade') usedModules.push(EffectFade);

            this.instance = new Swiper(this.el, {...config, modules: usedModules});
        } catch (e) {
            // Fail silently in prototype; optionally log
            console.error('[phi] Failed to load Swiper', e);
        }

        window.addEventListener('resize', () => {
            if (this.instance && typeof this.instance.update === 'function') {
                this.instance.update();
            }
        });
    }

	equalizeSwiperSlideHeights() {
        let rows = {};
        const colCount = this.element.dataset.columns || 2;

        this.element.querySelectorAll('.swiper-slide').forEach((slide, index) => {
            let rowIndex = Math.floor(index / colCount);
            if (!rows[rowIndex]) rows[rowIndex] = [];
            rows[rowIndex].push(slide);
        });

        Object.values(rows).forEach(rowSlides => {
            let maxHeight = Math.max(...rowSlides.map(slide => slide.offsetHeight));
            rowSlides.forEach(slide => (slide.style.height = `${maxHeight}px`));
        });
    }

    destroy() {
        if (this.instance) this.instance.destroy(true, true);
        this.instance = null;
    }
}

export {PRESETS as SWIPER_PRESETS};
